/**
 * CraftYourReadingJourney — parchment row-cards (Page 2 of master mockup).
 *
 * Layout: section eyebrow + serif title, then 4 vertical row-cards on parchment.
 * Each row-card: brass medallion (with stage seal) on the LEFT,
 *                title + italic tagline + description in the MIDDLE,
 *                rust pill "Enter Journey" + count badge on the RIGHT.
 */
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import { ArrowRight } from "lucide-react";
import { JOURNEYS } from "../data/journeys";
import "../styles/Parchment.css";
import "../styles/CraftJourney.css";

// Stage motto used as one-line punchline beneath each journey title.
const JOURNEY_PUNCHLINES = {
  aarohan:       "Rise. Grow. Evolve.",
  nirmaan:       "Build. Create. Contribute.",
  "nava-aarambh": "Begin. Repair. Renew.",
  tattva:        "Understand. Reflect. Realise.",
};

// Stage purpose (one short sentence) — matches the "For readers …" copy from the mockup.
const JOURNEY_PURPOSE = {
  aarohan:       "For readers seeking discipline, resilience and self-mastery.",
  nirmaan:       "For readers building their dreams, work and impact.",
  "nava-aarambh": "For readers starting a new chapter in life.",
  tattva:        "For readers seeking timeless wisdom and inner clarity.",
};

// Per-journey landscape illustration (painterly PNG generated via Nano Banana, /public/illustrations/{slug}.png).
const JOURNEY_ART = {
  aarohan:        "/illustrations/aarohan.png",
  nirmaan:        "/illustrations/nirmaan.png",
  "nava-aarambh": "/illustrations/nava-aarambh.png",
  tattva:         "/illustrations/tattva.png",
};

// Simple sigil glyphs drawn into the brass medallion — pure SVG, no images.
function JourneySigil({ slug, size = 64 }) {
  const stroke = "#2C1A12";
  const sw = 1.6;
  const cx = size / 2;
  const cy = size / 2;
  switch (slug) {
    case "aarohan":
      // Mountain peak + sun
      return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
          <circle cx={cx} cy={cy - 6} r="4" fill={stroke} />
          <path d={`M ${cx - 16} ${cy + 10} L ${cx - 4} ${cy - 4} L ${cx + 4} ${cy + 4} L ${cx + 16} ${cy + 10} Z`}
                fill="none" stroke={stroke} strokeWidth={sw} strokeLinejoin="round" />
        </svg>
      );
    case "nirmaan":
      // Temple / pillared structure
      return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
          <path d={`M ${cx - 16} ${cy + 12} L ${cx - 16} ${cy - 6} L ${cx} ${cy - 16} L ${cx + 16} ${cy - 6} L ${cx + 16} ${cy + 12}`}
                fill="none" stroke={stroke} strokeWidth={sw} strokeLinejoin="round" />
          <line x1={cx - 10} y1={cy - 2} x2={cx - 10} y2={cy + 12} stroke={stroke} strokeWidth={sw} />
          <line x1={cx} y1={cy - 6} x2={cx} y2={cy + 12} stroke={stroke} strokeWidth={sw} />
          <line x1={cx + 10} y1={cy - 2} x2={cx + 10} y2={cy + 12} stroke={stroke} strokeWidth={sw} />
        </svg>
      );
    case "nava-aarambh":
      // Sunrise over horizon
      return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
          <line x1={cx - 18} y1={cy + 8} x2={cx + 18} y2={cy + 8} stroke={stroke} strokeWidth={sw} />
          <path d={`M ${cx - 12} ${cy + 8} A 12 12 0 0 1 ${cx + 12} ${cy + 8}`}
                fill="none" stroke={stroke} strokeWidth={sw} />
          <line x1={cx - 16} y1={cy} x2={cx - 13} y2={cy - 3} stroke={stroke} strokeWidth={sw} />
          <line x1={cx + 16} y1={cy} x2={cx + 13} y2={cy - 3} stroke={stroke} strokeWidth={sw} />
          <line x1={cx} y1={cy - 8} x2={cx} y2={cy - 12} stroke={stroke} strokeWidth={sw} />
        </svg>
      );
    case "tattva":
    default:
      // Bodhi tree
      return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
          <line x1={cx} y1={cy + 14} x2={cx} y2={cy - 2} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
          <path d={`M ${cx - 14} ${cy - 4} Q ${cx} ${cy - 22} ${cx + 14} ${cy - 4}`}
                fill="none" stroke={stroke} strokeWidth={sw} />
          <path d={`M ${cx - 10} ${cy - 8} Q ${cx} ${cy - 18} ${cx + 10} ${cy - 8}`}
                fill="none" stroke={stroke} strokeWidth={sw} />
        </svg>
      );
  }
}

export default function CraftYourReadingJourney() {
  const navigate = useNavigate();

  return (
    <section
      className="cj-section parchment-theme"
      id="craft-journey"
      aria-label="Craft Your Reading Journey"
      data-testid="craft-journey-section"
    >
      <header className="cj-header">
        <span className="pt-eyebrow">CRAFT YOUR READING JOURNEY</span>
        <h2 className="cj-title">Choose Your Path.</h2>
        <p className="pt-tagline">Choose the path that aligns with your current season of life.</p>
        <div className="pt-divider"><span /><em>❦</em><span /></div>
      </header>

      <ul className="cj-rowcards" data-testid="cj-rowcards">
        {JOURNEYS.map((j, i) => (
          <motion.li
            key={j.key}
            className="pt-card cj-rowcard"
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`cj-rowcard-${j.key}`}
            onClick={() => navigate(`/journeys/${j.key}`)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(`/journeys/${j.key}`);
              }
            }}
            aria-label={`${j.title} — ${j.tagline}. ${j.description}`}
          >
            <div className="cj-rowcard-sigil-wrap" aria-hidden="true">
              <div className="pt-medallion pt-medallion-lg cj-rowcard-medallion">
                <JourneySigil slug={j.key} size={64} />
              </div>
            </div>

            <div className="cj-rowcard-body">
              <div className="cj-rowcard-titleblock">
                <h3 className="cj-rowcard-title">{j.title.toUpperCase()}</h3>
                <span className="cj-rowcard-hindi indic">{j.hindi}</span>
              </div>
              <p className="cj-rowcard-punchline">{JOURNEY_PUNCHLINES[j.key] || j.tagline}</p>
              <p className="cj-rowcard-purpose">{JOURNEY_PURPOSE[j.key] || j.description}</p>
            </div>

            <div className="cj-rowcard-action">
              <Link
                to={`/journeys/${j.key}`}
                className="pt-btn-rust cj-rowcard-cta"
                onClick={(e) => e.stopPropagation()}
                data-testid={`cj-rowcard-cta-${j.key}`}
              >
                Enter Journey <ArrowRight size={14} />
              </Link>
              <span className="cj-rowcard-count" data-testid={`cj-rowcard-count-${j.key}`}>
                {j.bookCount}
              </span>
            </div>

            <div className="cj-rowcard-thumb" aria-hidden="true">
              <img
                src={`${process.env.PUBLIC_URL || ""}${JOURNEY_ART[j.key]}`}
                alt=""
                loading="lazy"
                className="cj-rowcard-thumb-img"
              />
            </div>
          </motion.li>
        ))}
      </ul>
    </section>
  );
}
